/**
 * Attributes Store
 * 
 * Manages attribute schema definitions for the prioritization system.
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import type { Attribute } from '../../types/prioritization';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  deleteRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  toDbFormat,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';

const TABLE_NAME = 'attributes';

function isAttributeRowUuid(s: string | undefined): boolean {
  return !!s && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/**
 * Extract only the columns that exist in the Supabase attributes table.
 * Everything else (scales, ownerId, contextScope, inheritable, etc.)
 * is stored in the `metadata` JSONB column.
 */
function toDbAttributes(attr: Partial<Attribute>): Record<string, unknown> {
  const { key, label, description, type, options, required } = attr;

  // Pack extended fields into metadata so they round-trip through the DB
  const metadata: Record<string, unknown> = {
    ...(typeof (attr as any).metadata === 'object' ? (attr as any).metadata : {}),
  };
  if (attr.ownerId !== undefined) metadata.ownerId = attr.ownerId;
  if (attr.ownerName !== undefined) metadata.ownerName = attr.ownerName;
  if (attr.scales !== undefined) metadata.scales = attr.scales;
  if (attr.defaultScaleId !== undefined) metadata.defaultScaleId = attr.defaultScaleId;
  if (attr.semanticType !== undefined) metadata.semanticType = attr.semanticType;
  if (attr.contextScope !== undefined) metadata.contextScope = attr.contextScope;
  if (attr.inheritable !== undefined) metadata.inheritable = attr.inheritable;
  if (attr.overridable !== undefined) metadata.overridable = attr.overridable;
  if (attr.proposerType !== undefined) metadata.proposerType = attr.proposerType;
  if (attr.displayType !== undefined) metadata.displayType = attr.displayType;
  if (attr.category !== undefined) metadata.category = attr.category;
  if (attr.dialConfig !== undefined) metadata.dialConfig = attr.dialConfig;
  if (attr.dialPoints !== undefined) metadata.dialPoints = attr.dialPoints;
  if (attr.proposerRoles !== undefined) metadata.proposerRoles = attr.proposerRoles;
  if (attr.governance !== undefined) metadata.governance = attr.governance;
  if (attr.isCustomField !== undefined) metadata.isCustomField = attr.isCustomField;
  if (attr.enabled !== undefined) metadata.enabled = attr.enabled;
  if (attr.objectType !== undefined) metadata.objectType = attr.objectType;
  if (attr.placement !== undefined) metadata.placement = attr.placement;
  if (attr.usable_as_criterion !== undefined) metadata.usable_as_criterion = attr.usable_as_criterion;
  if ((attr as any).customDisplayType !== undefined) metadata.customDisplayType = (attr as any).customDisplayType;
  if ((attr as any).parentKey !== undefined) metadata.parentKey = (attr as any).parentKey;
  if ((attr as any).conditionalOptions !== undefined) metadata.conditionalOptions = (attr as any).conditionalOptions;

  return { key, label, description: description ?? null, definition: (attr as any).definition ?? null, is_system: (attr as any).isSystem ?? false, type, options: options ?? null, required: required ?? false, metadata };
}

/**
 * Hydrate an Attribute from a DB row by unpacking metadata fields.
 */
function fromDbRow(row: Record<string, unknown>): Attribute {
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  return {
    id: row.id as string | undefined,
    ...(row.__inheritedFromShared === true ? { inheritedFromShared: true } : {}),
    key: row.key as string,
    ...(typeof row.objective_id === 'string' ? { objectiveId: row.objective_id } : {}),
    label: row.label as string,
    description: meta.description as string | undefined ?? row.description as string | undefined,
    definition: row.definition as string | undefined,
    isSystem: row.is_system as boolean | undefined ?? false,
    type: row.type as Attribute['type'],
    /** DB may store NULL; coerce so merges and UI never treat missing as "keep old true". */
    required: row.required === true,
    options: row.options as string[] | undefined,
    ownerId: meta.ownerId as string | undefined,
    ownerName: meta.ownerName as string | undefined,
    scales: meta.scales as any | undefined,
    defaultScaleId: meta.defaultScaleId as string | undefined,
    semanticType: meta.semanticType as any,
    contextScope: meta.contextScope as any,
    inheritable: meta.inheritable as boolean ?? true,
    overridable: meta.overridable as boolean ?? true,
    proposerType: meta.proposerType as any,
    displayType: meta.displayType as any,
    category: meta.category as string | undefined,
    dialConfig: meta.dialConfig as any,
    dialPoints: meta.dialPoints as any,
    proposerRoles: meta.proposerRoles as any,
    governance: meta.governance as any,
    scope: (meta.scope as any) ?? 'global',
    isCustomField: meta.isCustomField as boolean | undefined,
    enabled: meta.enabled as boolean | undefined,
    objectType: meta.objectType as string | undefined,
    placement: meta.placement as Attribute['placement'],
    usable_as_criterion: meta.usable_as_criterion as boolean | undefined,
    customDisplayType: meta.customDisplayType as any,
    parentKey: meta.parentKey as string | undefined,
    conditionalOptions: meta.conditionalOptions as Record<string, string[]> | undefined,
  } as Attribute;
}

// Track if initial fetch has been triggered (module-level to persist across renders)
interface AttributesState {
  attributes: Attribute[];
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  syncToSupabase: (attribute: Attribute) => Promise<{ success: boolean; error?: string }>;
  
  // Actions — all return { error } on DB failure (optimistic rollback already applied)
  addAttribute: (attribute: Attribute) => Promise<{ error?: string }>;
  updateAttribute: (key: string, updates: Partial<Attribute>) => Promise<{ error?: string }>;
  deleteAttribute: (key: string) => Promise<{ error?: string }>;
  /** Reorder scoring attributes by supplying the new ordered array of keys. Updates display_order in DB. */
  reorderAttributes: (orderedKeys: string[]) => Promise<{ error?: string }>;
  
  // Getters
  getAttributeByKey: (key: string) => Attribute | undefined;
  getRequiredAttributes: () => Attribute[];
  getOptionalAttributes: () => Attribute[];
  /** Returns all custom fields (isCustomField === true), regardless of enabled state. */
  getCustomFields: () => Attribute[];
  /** Returns custom fields that are currently enabled. Optionally filter by objectType (defaults to 'item'). */
  getEnabledCustomFields: (objectType?: string) => Attribute[];
}

// Default scales for evaluative attributes (same idea as item category → frames: choose scale, then answer in that frame)
const DEFAULT_SCALE_LMH = {
  id: 'default-lmh',
  label: 'Low / Medium / High',
  scaleType: 'ordinal' as const,
  points: [
    { value: 1, label: 'Low', color: '#22c55e' },
    { value: 2, label: 'Medium', color: '#f59e0b' },
    { value: 3, label: 'High', color: '#ef4444' },
  ],
  isDefault: true,
};
const DEFAULT_SCALE_1_5 = {
  id: 'default-1-5',
  label: '1–5 Numeric',
  scaleType: 'numeric' as const,
  min: 1,
  max: 5,
  step: 1,
  points: [1, 2, 3, 4, 5].map((v) => ({ value: v, label: String(v) })),
  isDefault: false,
};
const DEFAULT_SCALE_TSHIRT = {
  id: 'default-tshirt',
  label: 'T-Shirt (XS–XL)',
  scaleType: 'ordinal' as const,
  points: [
    { value: 1, label: 'XS' },
    { value: 2, label: 'S' },
    { value: 3, label: 'M' },
    { value: 4, label: 'L' },
    { value: 5, label: 'XL' },
  ],
  isDefault: false,
};

// Default attributes: Impact, Effort, Urgency have multiple scales (like item category has multiple frames)
const DEFAULT_ATTRIBUTES: Attribute[] = [
  {
    key: 'impact',
    label: 'Impact',
    definition: 'The magnitude of effect this item would have on the outcome we care about. Higher impact means greater positive change if the item is executed.',
    isSystem: true,
    scope: 'global',
    inheritable: true,
    overridable: true,
    type: 'select',
    required: true,
    options: ['Low', 'Medium', 'High'],
    scales: [DEFAULT_SCALE_LMH, DEFAULT_SCALE_1_5, DEFAULT_SCALE_TSHIRT],
    defaultScaleId: 'default-lmh',
  },
  {
    key: 'effort',
    label: 'Effort',
    definition: 'The total cost of doing this — time, people, complexity. Higher effort means more resources required.',
    isSystem: true,
    scope: 'global',
    inheritable: true,
    overridable: true,
    type: 'select',
    required: true,
    options: ['Low', 'Medium', 'High'],
    scales: [DEFAULT_SCALE_LMH, DEFAULT_SCALE_1_5, DEFAULT_SCALE_TSHIRT],
    defaultScaleId: 'default-lmh',
  },
  {
    key: 'urgency',
    label: 'Urgency',
    definition: 'How time-sensitive this item is. Higher urgency means delay has increasing costs or closing windows.',
    isSystem: true,
    scope: 'global',
    inheritable: true,
    overridable: true,
    type: 'select',
    required: true,
    options: ['Low', 'Medium', 'High'],
    scales: [DEFAULT_SCALE_LMH, DEFAULT_SCALE_1_5, DEFAULT_SCALE_TSHIRT],
    defaultScaleId: 'default-lmh',
  },
  {
    key: 'risk',
    label: 'Risk',
    definition: 'The probability and severity of negative outcomes if this item is chosen or deprioritized.',
    isSystem: true,
    scope: 'global',
    inheritable: true,
    overridable: true,
    type: 'select',
    required: false,
    options: ['Low', 'Medium', 'High'],
  },
  {
    key: 'theme',
    label: 'Theme',
    definition: 'A grouping label that connects this item to a strategic theme, initiative, or area of focus.',
    isSystem: true,
    scope: 'global',
    inheritable: true,
    overridable: true,
    type: 'text',
    required: false,
  },
  {
    key: 'owner',
    label: 'Owner',
    definition: 'The person or team accountable for this item\'s execution.',
    isSystem: true,
    scope: 'global',
    inheritable: true,
    overridable: true,
    type: 'text',
    required: false,
  },
];

export const useAttributesStore = create<AttributesState>()(
  (set, get) => ({
    attributes: DEFAULT_ATTRIBUTES,
    syncState: createInitialSyncState(),
      
      // ======================================================================
      // Supabase Sync Actions
      // ======================================================================
      
      fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[AttributesStore] Supabase not available');
        return;
      }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const result = await fetchAll<Attribute>(TABLE_NAME, { column: 'display_order', ascending: true }, { filterByWorkspace: true, strictWorkspaceFilter: true });

        // A built-in the workspace has not made its own is read from its shared row
        // (workspace_id null) — the definition every workspace inherits — rather than
        // the copy compiled into the app, so an edit saved anywhere is the one shown.
        if (!result.error) {
          const sharedClient = getSafeSupabaseClient();
          const ownKeys = new Set(((result.data ?? []) as Array<{ key?: string }>).map((r) => r.key));
          const builtInKeys = DEFAULT_ATTRIBUTES.map((a) => a.key).filter((k) => !ownKeys.has(k));
          if (sharedClient && getCurrentWorkspaceId() && builtInKeys.length > 0) {
            const { data: sharedRows } = await sharedClient
              .from(TABLE_NAME)
              .select('*')
              .is('workspace_id', null)
              .in('key', builtInKeys);
            if (sharedRows?.length) {
              result.data = [
                ...((result.data ?? []) as unknown[]),
                ...sharedRows.map((r) => ({ ...(r as Record<string, unknown>), __inheritedFromShared: true })),
              ] as typeof result.data;
            }
          }
        }

        if (result.error) {
          set({ 
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              syncError: result.error 
            } 
          });
          console.error('[AttributesStore] Failed to fetch from Supabase:', result.error);
          return;
        }
        
        // Replace strategy: Supabase is source of truth
        if (result.data && result.data.length > 0) {
          // Hydrate metadata fields back into Attribute shape
          let hydrated = result.data.map((row) => fromDbRow(row as any));
          // Backfill default scales for impact/effort/urgency if DB rows have no scales (e.g. from older seed)
          const keysWithDefaultScales = ['impact', 'effort', 'urgency'];
          hydrated = hydrated.map((a) => {
            if (!keysWithDefaultScales.includes(a.key) || (a.scales && a.scales.length > 0)) return a;
            return { ...a, scales: [DEFAULT_SCALE_LMH, DEFAULT_SCALE_1_5, DEFAULT_SCALE_TSHIRT], defaultScaleId: 'default-lmh' };
          });
          const supabaseKeys = new Set(hydrated.map(a => a.key));
          const existingAttributes = get().attributes;
          const localOnlyAttributes = existingAttributes.filter(a => !supabaseKeys.has(a.key));
          const mergedAttributes = [...hydrated, ...localOnlyAttributes];
          
          set({ 
            attributes: mergedAttributes,
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              lastSyncAt: new Date(),
              syncError: null 
            }
          });
          console.log(`[AttributesStore] Loaded ${result.data.length} from Supabase, kept ${localOnlyAttributes.length} local-only. Total: ${mergedAttributes.length}`);
        } else {
          // No Supabase data - ensure local defaults are available and seed to Supabase
          console.log('[AttributesStore] No attributes from Supabase, using defaults...');
          
          // CRITICAL: Ensure local state has defaults (prevents "zero items" UI issue)
          const currentAttributes = get().attributes;
          if (currentAttributes.length === 0) {
            console.log('[AttributesStore] Local attributes empty, restoring defaults');
            set({ attributes: DEFAULT_ATTRIBUTES });
          }
          
          // Reached only when the workspace has no rows and there are no shared rows to
          // inherit. Seed the workspace's own copies (one per key per workspace, migration 949);
          // use toDbAttributes so metadata (scales, defaultScaleId) persists.
          const supabase = getSafeSupabaseClient();
          const workspaceId = getCurrentWorkspaceId();
          if (supabase && workspaceId) {
            const dbRecords = DEFAULT_ATTRIBUTES.map((attr, index) =>
              toDbFormat({
                ...toDbAttributes(attr),
                display_order: index,
                workspace_id: workspaceId,
              } as Record<string, unknown>)
            );
            const { error } = await supabase
              .from(TABLE_NAME)
              .upsert(dbRecords, { onConflict: 'workspace_id,key', ignoreDuplicates: true });
            if (error) {
              console.warn('[AttributesStore] Seed upsert failed (non-blocking):', error.message);
            } else {
              console.log(`[AttributesStore] Seeded ${DEFAULT_ATTRIBUTES.length} attributes to Supabase (upsert on key)`);
            }
          }
          
          set({ 
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              lastSyncAt: new Date(),
              syncError: null // Don't treat seed failures as blocking errors
            }
          });
        }
      },
      
      syncToSupabase: async (attribute: Attribute) => {
        if (!isSupabaseAvailable()) {
          return { success: true };
        }
        
        set({ syncState: { ...get().syncState, isSyncing: true } });
        
        const recordId = attribute.id ?? attribute.key;
        const result = await updateRecord(TABLE_NAME, recordId, toDbAttributes(attribute) as any);
        
        set({ syncState: { ...get().syncState, isSyncing: false } });
        
        if (result.error) {
          console.error('[AttributesStore] Failed to sync to Supabase:', result.error);
          return { success: false, error: result.error };
        }
        
        return { success: true };
      },
      
      // ======================================================================
      // CRUD Actions
      // ======================================================================
      
      addAttribute: async (attribute) => {
        // Prevent duplicate keys
        if (get().attributes.some(a => a.key === attribute.key)) {
          throw new Error(`Attribute with key "${attribute.key}" already exists`);
        }
        
        // Update local state first (optimistic update)
        set((state) => ({
          attributes: [...state.attributes, attribute]
        }));
        
        // Then sync to Supabase (only DB-safe columns); merge returned id into state
        if (isSupabaseAvailable()) {
          const result = await insertRecord(TABLE_NAME, toDbAttributes(attribute) as any);
          if (result.error) {
            // Roll back optimistic addition
            set((state) => ({
              attributes: state.attributes.filter(a => a.key !== attribute.key),
            }));
            // 23505 = unique_violation: attribute already exists in DB but wasn't in local state.
            // Fetch the existing row so local state is reconciled; don't show an error toast.
            const errorCode = typeof result.error === 'object' && result.error !== null
              ? (result.error as any).code
              : null;
            if (errorCode === '23505') {
              const supabase = getSafeSupabaseClient();
              const workspaceId = getCurrentWorkspaceId();
              if (supabase) {
                let q = supabase.from(TABLE_NAME).select('*').eq('key', attribute.key);
                if (workspaceId) q = q.eq('workspace_id', workspaceId);
                const { data: existing } = await q.maybeSingle();
                if (existing) {
                  const hydrated = fromDbRow(existing as Record<string, unknown>);
                  set((state) => ({
                    attributes: state.attributes.some(a => a.key === hydrated.key)
                      ? state.attributes
                      : [...state.attributes, hydrated],
                  }));
                }
              }
              return { alreadyExists: true };
            }
            dispatchStoreError('Attributes', 'add', result.error);
            return { error: result.error };
          }
          if (result.data?.id) {
            set((state) => ({
              attributes: state.attributes.map((a) =>
                a.key === attribute.key ? { ...a, id: result.data!.id } : a
              ),
            }));
          }
        }
        return {};
      },
      
      updateAttribute: async (key, updates) => {
        const attr = get().attributes.find(a => a.key === key);
        if (!attr) return {};
        
        const updated = { ...attr, ...updates };
        
        // Update local state first (optimistic update)
        set((state) => ({
          attributes: state.attributes.map(a => a.key === key ? updated : a)
        }));
        
        // Sync to Supabase — match by UUID id when available, otherwise match by key + workspace_id.
        // Fallback ladder: stale client id, or DB row with workspace_id null while client filters by workspace.
        if (isSupabaseAvailable()) {
          const supabase = getSafeSupabaseClient();
          if (supabase) {
            const dbPayload = toDbAttributes(updated) as Record<string, unknown>;
            const workspaceId = getCurrentWorkspaceId();

            const rowCount = (data: unknown) =>
              Array.isArray(data) ? data.length : data ? 1 : 0;

            // Each attempt actually returns the result of .update(...).eq(...)
            // (a PostgrestFilterBuilder), not the bare .from(...) result (a
            // PostgrestQueryBuilder) — the old annotation named the wrong
            // link in the chain, which is why every attempt below reported
            // as missing insert/upsert/update/delete (those only exist on
            // the pre-.update() builder).
            const attempts: Array<() => ReturnType<ReturnType<NonNullable<typeof supabase>['from']>['update']>> = [];

            let updatedRows: unknown = null;
            let lastError: { message: string } | null = null;

            if (workspaceId) {
              // In a workspace, a save changes that workspace's own definition. A
              // built-in it has not made its own (read from the shared row, or the
              // copy compiled into the app) becomes its own copy now — never an edit
              // to the shared row every workspace inherits (migration 949).
              const own = await supabase
                .from(TABLE_NAME)
                .update(dbPayload)
                .eq('key', key)
                .eq('workspace_id', workspaceId)
                .select('id');
              if (own.error) {
                lastError = own.error;
              } else if (rowCount(own.data) > 0) {
                updatedRows = own.data;
              } else {
                const copy = await supabase
                  .from(TABLE_NAME)
                  .insert({ ...dbPayload, workspace_id: workspaceId })
                  .select('id');
                if (copy.error) lastError = copy.error;
                else updatedRows = copy.data;
              }
            } else {
              if (isAttributeRowUuid(attr.id)) {
                attempts.push(() => supabase.from(TABLE_NAME).update(dbPayload).eq('id', attr.id!));
              }
              attempts.push(() =>
                supabase.from(TABLE_NAME).update(dbPayload).eq('key', key).is('workspace_id', null)
              );

              for (const run of attempts) {
                const { data, error } = await run().select('id');
                if (error) {
                  lastError = error;
                  break;
                }
                if (rowCount(data) > 0) {
                  updatedRows = data;
                  lastError = null;
                  break;
                }
              }
            }

            if (lastError) {
              set((state) => ({
                attributes: state.attributes.map(a => a.key === key ? attr : a),
              }));
              dispatchStoreError('Attributes', 'update', lastError.message);
              return { error: lastError.message };
            }
            if (rowCount(updatedRows) === 0) {
              set((state) => ({
                attributes: state.attributes.map(a => a.key === key ? attr : a),
              }));
              const msg =
                'Attribute update did not match any database row (check workspace or refresh the page).';
              dispatchStoreError('Attributes', 'update', msg);
              return { error: msg };
            }

            // Reconcile primary key if we succeeded via key-based match but client had a wrong/missing id
            const firstId = Array.isArray(updatedRows) ? (updatedRows as { id?: string }[])[0]?.id : undefined;
            if ((firstId && firstId !== attr.id) || (workspaceId && updated.inheritedFromShared)) {
              set((state) => ({
                attributes: state.attributes.map((a) =>
                  a.key === key ? { ...updated, id: firstId ?? updated.id, inheritedFromShared: undefined } : a
                ),
              }));
            }
          }
        }
        return {};
      },
      
      deleteAttribute: async (key) => {
        // Don't allow deleting required attributes that might be in use
        const attr = get().attributes.find(a => a.key === key);
        if (attr?.required) {
          throw new Error(`Cannot delete required attribute "${key}". Make it optional first.`);
        }
        
        // Use attribute.id (UUID) when present so Supabase tables with UUID primary key work; fallback to key for key-based tables
        const recordId = attr?.id ?? key;
        
        // Update local state first (optimistic update)
        set((state) => ({
          attributes: state.attributes.filter(a => a.key !== key)
        }));
        
        // Then sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await deleteRecord(TABLE_NAME, recordId);
          if (result.error) {
            // Roll back optimistic deletion
            if (attr) {
              set((state) => ({
                attributes: [...state.attributes, attr],
              }));
            }
            dispatchStoreError('Attributes', 'delete', result.error);
            return { error: result.error };
          }
        }
        return {};
      },
      
      reorderAttributes: async (orderedKeys) => {
        const currentAttrs = get().attributes;
        const scoringByKey = Object.fromEntries(
          currentAttrs.filter(a => !a.isCustomField).map(a => [a.key, a])
        );
        const nonScoring = currentAttrs.filter(a => a.isCustomField);

        // Optimistically reorder local state
        const reordered = [
          ...orderedKeys.map(k => scoringByKey[k]).filter(Boolean),
          ...nonScoring,
        ];
        set({ attributes: reordered });

        // Persist display_order for each attribute
        const supabase = getSafeSupabaseClient();
        const workspaceId = getCurrentWorkspaceId();
        if (supabase) {
          const results = await Promise.all(
            orderedKeys.map(async (key, index) => {
              const attr = scoringByKey[key];
              if (!attr) return undefined;
              // An inherited built-in's id is the shared row's; its order is the workspace's own to set.
              const ownRow = isAttributeRowUuid(attr.id) && !attr.inheritedFromShared;
              let q = ownRow
                ? supabase.from(TABLE_NAME).update({ display_order: index }).eq('id', attr.id!)
                : supabase.from(TABLE_NAME).update({ display_order: index }).eq('key', key);
              if (!ownRow && workspaceId) {
                q = (q as any).eq('workspace_id', workspaceId);
              }
              const { data, error } = await q.select('id');
              if (error) return error.message;
              const n = Array.isArray(data) ? data.length : data ? 1 : 0;
              if (n === 0) return 'display_order update matched no row';
              return undefined;
            })
          );
          const firstError = results.find(Boolean);
          if (firstError) return { error: firstError };
        }
        return {};
      },

      getAttributeByKey: (key) => 
        get().attributes.find(attr => attr.key === key),
      
      getRequiredAttributes: () => 
        get().attributes.filter(attr => attr.required),
      
      getOptionalAttributes: () =>
        get().attributes.filter(attr => !attr.required),

      getCustomFields: () =>
        get().attributes.filter(attr => attr.isCustomField === true),

      /** Returns custom fields that are enabled. Optionally filter by objectType (defaults to 'item'). */
      getEnabledCustomFields: (objectType = 'item') =>
        get().attributes.filter(
          attr =>
            attr.isCustomField === true &&
            attr.enabled !== false &&
            (attr.objectType ?? 'item') === objectType
        ),
  })
);

// Initial fetch is driven by useSupabaseSync (App.tsx) to avoid duplicate requests and AbortErrors.